/**
 * TBB Boost Post-Render Hooks
 * ───────────────────────────
 * Lightweight post-processing for Boost TURBO product cards.
 *
 * With the TBB template changes (product-item-main, product-image,
 * product-info), Boost now natively renders:
 *   - Decade badges (via custom.decade metafield in product-info.liquid)
 *   - Title in Polaroid frame strip (via product-image.liquid)
 *   - Built-in frame gating class (via custom.has_builtin_frame in main.liquid)
 *
 * This JS file now ONLY handles:
 *   1. Sale badge normalization ("Sale" → "X% off") — can't be done in Liquid
 *      because Boost calculates sale labels from its own logic.
 *   2. Recommendation widget title relocation — the recommendation widget uses
 *      a different template path than the collection grid, so it still needs
 *      JS-based title movement.
 *   3. Trackpad horizontal scroll → Slick carousel navigation on the
 *      recommendation widget.
 *
 * Uses the official Boost TURBO API:
 *   boostWidgetIntegration.regisCustomization(fn, scope)
 * instead of MutationObserver / polling / API re-fetching.
 *
 * Dependencies: jQuery (loaded by Boost for Slick carousel API).
 * Loaded by: layout/theme.liquid (all pages — Boost recs appear on index, collection, PDP).
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════
     SALE BADGE NORMALIZATION
     Rewrites Boost sale labels to "X% off" format to match TBB style.
     ══════════════════════════════════════════════════════════════════ */

  function rewriteSaleBadges(scope) {
    var root = scope || document;
    var labels = root.querySelectorAll(
      '.boost-sd__product-label--sale:not([data-tbb-badge-fixed]),' +
      '.boost-sd__product-label[class*="sale"]:not([data-tbb-badge-fixed]),' +
      '.boost-sd__product-badge--sale:not([data-tbb-badge-fixed])'
    );
    labels.forEach(function (label) {
      label.setAttribute('data-tbb-badge-fixed', '1');
      var text = (label.textContent || '').trim();

      // Already in "X% off" format
      if (/^\d+%\s+off$/i.test(text)) return;

      // "-X%" format → "X% off"
      var pctMatch = text.match(/^-?(\d+)%$/);
      if (pctMatch) {
        label.textContent = pctMatch[1] + '% off';
        return;
      }

      // "Sale" / "SALE" → calculate from prices on card
      var card = label.closest('.boost-sd__product-item');
      if (!card) return;

      var saleEl = card.querySelector('.boost-sd__product-price--sale');
      var compareEl = card.querySelector(
        '.boost-sd__product-price--compare, .boost-sd__product-price del'
      );

      if (saleEl && compareEl) {
        var sale = parseFloat((saleEl.textContent || '').replace(/[^0-9.]/g, ''));
        var compare = parseFloat((compareEl.textContent || '').replace(/[^0-9.]/g, ''));
        if (compare > 0 && sale < compare) {
          var pct = Math.round(100 - (sale / compare * 100));
          label.textContent = pct + '% off';
          return;
        }
      }

      // Fallback: normalize casing
      if (/^sale$/i.test(text)) {
        label.textContent = 'Sale';
      }
    });
  }

  /* ══════════════════════════════════════════════════════════════════
     RECOMMENDATION WIDGET — TITLE RELOCATION
     The recommendation widget uses a separate template that doesn't
     include TBB's modified product-image.liquid, so titles still need
     JS-based relocation into the image wrapper for Polaroid framing.
     ══════════════════════════════════════════════════════════════════ */

  function relocateTitlesInScope(scope) {
    var root = scope || document;
    var cards = root.querySelectorAll(
      '.boost-sd__product-item:not([data-tbb-title-moved])'
    );
    cards.forEach(function (card) {
      var titleEl = card.querySelector('.boost-sd__product-title');
      var imgWrapper = card.querySelector('.boost-sd__product-image-wrapper');
      if (!titleEl || !imgWrapper) return;
      // Skip if title is already inside the image wrapper (template-rendered)
      if (titleEl.closest('.boost-sd__product-image-wrapper')) {
        card.setAttribute('data-tbb-title-moved', '1');
        return;
      }
      // Move title into image wrapper
      titleEl.classList.add('tbb-boost-frame-title');
      if (!titleEl.querySelector('.tbb-boost-frame-title-inner')) {
        var inner = document.createElement('span');
        inner.className = 'tbb-boost-frame-title-inner';
        while (titleEl.firstChild) inner.appendChild(titleEl.firstChild);
        titleEl.appendChild(inner);
      }
      imgWrapper.appendChild(titleEl);
      card.setAttribute('data-tbb-title-moved', '1');
    });
  }

  /* ══════════════════════════════════════════════════════════════════
     BOOST SLICK CAROUSEL ENHANCEMENT
     Three-part upgrade to make Boost rec carousels feel as fluid as
     the native Swiper ones:
       A. Reconfigure Slick post-init (swipeToSlide, speed, easing)
       B. Real-time trackpad/wheel scroll with momentum carry
       C. Touch momentum for multi-slide flicking
     ══════════════════════════════════════════════════════════════════ */

  // ── Config ──
  var SLICK_SPEED = 260;
  var SLICK_CSS_EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  var WHEEL_SETTLE_MS = 120;
  var WHEEL_SNAP_SPEED = 200;
  var WHEEL_FRICTION = 0.92;
  var TOUCH_VELOCITY_THRESHOLD = 0.4;
  var TOUCH_VELOCITY_FACTOR = 2.5;
  var TOUCH_MAX_EXTRA_SLIDES = 4;
  var TOUCH_VELOCITY_WINDOW_MS = 80;
  var TOUCH_BASE_SPEED = 250;
  var TOUCH_SPEED_PER_SLIDE = 60;

  /**
   * A. Reconfigure Slick post-init via jQuery API.
   *    - swipeToSlide: allows swiping to any position, not just
   *      slidesToScroll increments — feels more natural on touch.
   *    - speed + cssEase: smoother, faster transitions.
   *    - touchThreshold: lower = more responsive to small swipes.
   */
  function reconfigureSlick(sliderEl) {
    if (typeof jQuery === 'undefined') return;
    var $slider = jQuery(sliderEl);
    if (!$slider.hasClass('slick-initialized')) return;
    try {
      $slider.slick('slickSetOption', 'swipeToSlide', true, false);
      $slider.slick('slickSetOption', 'speed', SLICK_SPEED, false);
      $slider.slick('slickSetOption', 'cssEase', SLICK_CSS_EASE, false);
      // touchThreshold=1: drag responds on the very first pixel of finger
      // movement instead of after Slick's default 8px deadzone. The
      // deadzone was the felt-delay between finger-touch-and-move and
      // the carousel starting to react. 1px is the minimum Slick allows.
      $slider.slick('slickSetOption', 'touchThreshold', 1, true);
    } catch (ex) {
      // Slick may not be fully ready — fail silently.
    }
  }
  // Note: an earlier version installed a Proxy on slickTrack.style to
  // block Slick's snap-on-momentum-end, but it triggered stack-trace
  // checks on every transform read/write — Slick fires hundreds per
  // touch frame, which made mobile swipe feel sticky/jittery. Removed.
  // Mid-momentum snap stutter is accepted as a Slick limitation; the
  // homepage rec widget is being replaced with a native Liquid
  // carousel where we own the entire scroll pipeline.

  /**
   * B. Real-time trackpad/wheel scroll with momentum.
   *    Replaces the old 300ms-cooldown button-click hack. Directly
   *    translates the .slick-track on every wheel event, then carries
   *    remaining velocity with exponential decay before snapping.
   */
  function bindWheelScroll(recWidget) {
    var slickList = recWidget.querySelector('.slick-list');
    var slickTrack = recWidget.querySelector('.slick-track');
    if (!slickList || !slickTrack) return;

    var sliderEl = recWidget.querySelector('.boost-sd__slider, .boost-sd__product-list-carousel');
    var $slider = (typeof jQuery !== 'undefined' && sliderEl) ? jQuery(sliderEl) : null;

    var velocity = 0;
    var settleTimeout = null;
    var momentumRaf = null;

    function getTrackPosition() {
      var transform = window.getComputedStyle(slickTrack).transform;
      if (!transform || transform === 'none') return 0;
      var parts = transform.match(/matrix.*\((.+)\)/);
      if (!parts) return 0;
      var values = parts[1].split(', ');
      return parseFloat(values[4]) || 0;
    }

    function setTrackPosition(px) {
      // Clamp to valid bounds so wheel/momentum scroll can't push the track
      // past slide 0 (positive) or past the rightmost full-page position
      // (more negative than -(trackWidth - listWidth)).
      var trackW = slickTrack.offsetWidth;
      var listW = slickList.offsetWidth;
      var minX = -(trackW - listW);
      if (minX > 0) minX = 0;
      if (px > 0) px = 0;
      if (px < minX) px = minX;
      slickTrack.style.transform = 'translate3d(' + px + 'px, 0px, 0px)';
      // Mark the timestamp of OUR last write. While we're actively
      // scrolling, Slick's internal setPosition (which fires on swipe-end
      // detection from wheel events, resize, etc.) gets blocked — see
      // the setPosition wrap below.
      window.__tbbLastTrackWrite = performance.now();
    }

    function snapToNearest() {
      if (!$slider || !$slider.hasClass('slick-initialized')) return;
      try {
        var slick = $slider.slick('getSlick');
        if (!slick) return;
        // Calculate which slide is closest to current position
        var pos = Math.abs(getTrackPosition());
        var slideWidth = slick.slideWidth || 1;
        var nearest = Math.round(pos / slideWidth);
        var maxSlide = slick.slideCount - slick.options.slidesToShow;
        nearest = Math.max(0, Math.min(nearest, maxSlide));
        $slider.slick('slickGoTo', nearest, false);
      } catch (ex) {}
    }

    function runMomentum() {
      // LeMarc directive: no snap-to-slide on any carousel — fully
      // organic scrolling. When velocity decays past threshold, the
      // track simply comes to rest wherever it is. No slickGoTo,
      // no animated snap to nearest. Same feel as native horizontal
      // scroll on a webpage.
      if (Math.abs(velocity) < 0.5) {
        momentumRaf = null;
        return;
      }
      var current = getTrackPosition();
      var next = current - velocity;
      setTrackPosition(next);
      velocity *= WHEEL_FRICTION;
      momentumRaf = requestAnimationFrame(runMomentum);
    }

    function stopMomentum() {
      if (momentumRaf) {
        cancelAnimationFrame(momentumRaf);
        momentumRaf = null;
      }
    }

    slickList.addEventListener('wheel', function (e) {
      var dx = e.deltaX;
      var dy = e.deltaY;
      var horizontalDominant = Math.abs(dx) > Math.abs(dy);

      if (!horizontalDominant && !e.shiftKey) return;
      e.preventDefault();
      stopMomentum();

      var delta = e.shiftKey && !horizontalDominant ? dy : dx;
      velocity = velocity * 0.3 + delta * 0.7;

      // Disable Slick's CSS transition during direct manipulation
      slickTrack.style.transition = 'none';
      var current = getTrackPosition();
      setTrackPosition(current - delta);

      if (settleTimeout) clearTimeout(settleTimeout);
      settleTimeout = setTimeout(function () {
        // No snap to re-enable transition for — momentum decays to rest
        // wherever it lands (organic scroll). Keep transition:none so
        // momentum frames don't get a 250ms easing applied to each step.
        momentumRaf = requestAnimationFrame(runMomentum);
      }, WHEEL_SETTLE_MS);
    }, { passive: false });
  }

  /**
   * C. Touch momentum — DISABLED per LeMarc directive (organic scroll only,
   * no snap-to-slide on any carousel). Slick's own touch handler still does
   * 1:1 finger drag during the gesture; we just don't add multi-slide snap
   * on release. The track comes to rest wherever Slick's natural decay
   * leaves it.
   *
   * Function kept as no-op shim so existing callers don't break.
   */
  function bindTouchMomentum(recWidget) {
    return;
  }

  // eslint-disable-next-line
  function bindTouchMomentum_LEGACY_DISABLED(recWidget) {
    var sliderEl = recWidget.querySelector('.boost-sd__slider, .boost-sd__product-list-carousel');
    var slickList = recWidget.querySelector('.slick-list');
    if (!sliderEl || !slickList) return;
    var $slider = (typeof jQuery !== 'undefined') ? jQuery(sliderEl) : null;
    if (!$slider) return;

    var history = [];
    var isTracking = false;
    var pendingFlick = null;

    slickList.addEventListener('touchstart', function (e) {
      if (pendingFlick) {
        cancelAnimationFrame(pendingFlick);
        pendingFlick = null;
      }
      history = [];
      isTracking = true;
      var touch = e.touches[0];
      history.push({ x: touch.clientX, t: Date.now() });
    }, { passive: true });

    slickList.addEventListener('touchmove', function (e) {
      if (!isTracking) return;
      var touch = e.touches[0];
      var now = Date.now();
      history.push({ x: touch.clientX, t: now });
      while (history.length > 2 && now - history[1].t > TOUCH_VELOCITY_WINDOW_MS) {
        history.shift();
      }
    }, { passive: true });

    slickList.addEventListener('touchend', function () {
      if (!isTracking) return;
      isTracking = false;
      if (history.length < 2) return;

      var first = history[0];
      var last = history[history.length - 1];
      var dt = last.t - first.t;
      if (dt === 0) return;

      var vx = (last.x - first.x) / dt;
      var absV = Math.abs(vx);
      if (absV < TOUCH_VELOCITY_THRESHOLD) return;

      var extraSlides = Math.min(
        Math.round((absV - TOUCH_VELOCITY_THRESHOLD) * TOUCH_VELOCITY_FACTOR),
        TOUCH_MAX_EXTRA_SLIDES
      );
      if (extraSlides < 1) return;

      var direction = vx < 0 ? 1 : -1;

      // rAF lets Slick's internal handler commit first, then we override.
      pendingFlick = requestAnimationFrame(function () {
        pendingFlick = null;
        try {
          var slick = $slider.slick('getSlick');
          if (!slick) return;
          var current = slick.currentSlide;
          var maxSlide = slick.slideCount - slick.options.slidesToShow;
          var target = Math.max(0, Math.min(
            current + direction * extraSlides,
            maxSlide
          ));
          if (target === current) return;
          $slider.slick('slickGoTo', target, false);
        } catch (ex) {}
      });
    }, { passive: true });
  }

  /**
   * Master bind — runs all three enhancements on a recommendation widget.
   */
  function enhanceBoostCarousel(recWidget) {
    if (recWidget.hasAttribute('data-tbb-scroll-bound')) return;
    recWidget.setAttribute('data-tbb-scroll-bound', '1');

    var sliderEl = recWidget.querySelector('.boost-sd__slider, .boost-sd__product-list-carousel');
    if (sliderEl) reconfigureSlick(sliderEl);
    bindWheelScroll(recWidget);
    bindTouchMomentum(recWidget);
  }

  /* ══════════════════════════════════════════════════════════════════
     BOOST TURBO LIFECYCLE HOOKS
     Official API: boostWidgetIntegration.regisCustomization()
     ══════════════════════════════════════════════════════════════════ */

  /**
   * afterRender — fires after Boost re-renders the product grid
   * (filter change, sort, pagination, initial load).
   * Only sale badge normalization is needed here; decade badges and
   * title positioning are handled at the Boost template level.
   */
  function afterRender() {
    rewriteSaleBadges(document);
  }

  /**
   * Register with Boost TURBO for the filter scope (collection grid).
   * We poll briefly for boostWidgetIntegration since Boost loads async.
   */
  function registerBoostHooks() {
    if (typeof boostWidgetIntegration !== 'undefined') {
      boostWidgetIntegration.regisCustomization(afterRender, 'filter');
      console.log('[TBB] Boost afterRender hook registered (filter scope).');
      return true;
    }
    return false;
  }

  /* ══════════════════════════════════════════════════════════════════
     INIT
     ══════════════════════════════════════════════════════════════════ */

  function init() {
    // Register Boost lifecycle hooks — retry briefly if Boost hasn't loaded yet
    if (!registerBoostHooks()) {
      var attempts = 0;
      var regInterval = setInterval(function () {
        attempts++;
        if (registerBoostHooks() || attempts > 20) {
          clearInterval(regInterval);
          if (attempts > 20) {
            console.warn('[TBB] boostWidgetIntegration not found after 10s. Falling back to one-time processing.');
            rewriteSaleBadges(document);
          }
        }
      }, 500);
    }

    // Recommendation widget — uses separate template, needs JS relocation.
    // Watch for it to appear and bind once.
    function processRecommendation() {
      var recWidget = document.querySelector('.boost-sd__recommendation');
      if (!recWidget || recWidget.hasAttribute('data-tbb-rec-processed')) return;
      recWidget.setAttribute('data-tbb-rec-processed', '1');

      relocateTitlesInScope(recWidget);
      rewriteSaleBadges(recWidget);
      enhanceBoostCarousel(recWidget);

      // Slick clones slides after init — brief retry to catch clones
      var burst = 0;
      var burstTimer = setInterval(function () {
        relocateTitlesInScope(recWidget);
        rewriteSaleBadges(recWidget);
        burst++;
        if (burst >= 6) clearInterval(burstTimer);
      }, 500);

      // Watch for future Slick re-renders (e.g. responsive breakpoint changes)
      var recObs = new MutationObserver(function () {
        relocateTitlesInScope(recWidget);
        rewriteSaleBadges(recWidget);
      });
      recObs.observe(recWidget, { childList: true, subtree: true });
    }

    // Poll for recommendation widget (loads independently, often late)
    var recPollCount = 0;
    var recPoll = setInterval(function () {
      processRecommendation();
      recPollCount++;
      if (recPollCount > 30) clearInterval(recPoll);
    }, 500);
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
